// Cio che la Pilotta PASSA al trasporto comune (`src/lib/gamehost/`): il `dominio` delle rotte, i posti ammessi, la mossa e come si compone l'azione, le frasi dei rifiuti. Il trasporto non conosce nessun gioco:
// e questo file che glielo dice. La forma e quella del tressette (TMA#823), con in piu la LICITA (le mosse `OFFRI`, `PASSO`, `KLEISTO`, `XANA_KLEISTO`) e il traguardo scelto da chi crea il tavolo.
//
// `componiAzione` e le liste sono costanti/funzioni DI MODULO (il trasporto tiene l'ultima callback in un ref e una lambda inline non riaprirebbe piu il socket).
import type { PostoPubblico } from '@/lib/gamehost/protocol';
import { creaTavoloBody, restDi } from '@/lib/gamehost/rest';
import type { TipoTavolo } from '@/lib/gamehost/rest';
import type { VociRifiuto } from '@/lib/gamehost/rifiuti';
import { ticketRoute } from '@/lib/gamehost/ticket';
import type { FacciaFrancese, SemeFrancese } from '@/lib/tavolo/carta';
import { MOTIVI, rangoDi, semeDi } from './protocol';
import type { AzioneDiPersona, Carta, Posto, Seme, Traguardo } from './protocol';

/** Il segmento di `/v1/games/<dominio>`: `limulo-app/gioco.js` di QPilotta, `dominio`. */
export const PILOTTA_DOMINIO = 'pilotta';
/** La Pilotta si gioca SOLO a quattro, in due coppie ({0,2} e {1,3}, compagni di fronte), SOLO amichevole (contratto di fronte, L-3902; `POSTI_AMMESSI` di `regole.ts` del game host). */
export const POSTI = 4;
export const POSTI_AMMESSI: readonly number[] = [POSTI];
/** Il traguardo predefinito di una partita (L-3903): 151. Lo sceglie chi crea il tavolo fra 151, 251 e 301 (`TRAGUARDI` di `lib/pilotta/protocol.ts`). */
export const TRAGUARDO_PREDEFINITO: Traguardo = 151;

export const PILOTTA_REST = restDi(PILOTTA_DOMINIO);
export const PILOTTA_TICKET_ROUTE = ticketRoute(PILOTTA_DOMINIO);

/** I tre livelli del robot (`LIVELLI_ROBOT` di `limulo-app/rotte.js` del gioco): il nome visibile e `livello.<L>` nel catalogo. */
export const LIVELLI_ROBOT = ['L0', 'L1', 'L2'] as const;
export type LivelloRobot = (typeof LIVELLI_ROBOT)[number];

/** La mossa di una persona: il posto lo mette il trasporto, dall'ultimo `table:state`. */
export type MossaPilotta =
  | { t: 'OFFRI'; valore: number; seme: Seme }
  | { t: 'PASSO' }
  | { t: 'KLEISTO' }
  | { t: 'XANA_KLEISTO' }
  | { t: 'GIOCA'; carta: Carta };

/**
 * Le chiavi dell'azione sono ESATTAMENTE quelle che il game host accetta (`CHIAVI_DELL_OFFERTA`, `CHIAVI_DEL_PASSO`, `CHIAVI_DELLA_CARTA` di `regole.ts`): un'altra, o un campo `undefined` messo a mano, e
 * `AZIONE_MALFORMATA`. Si compone da una `switch` con le chiavi scritte per esteso, mai da uno spread della mossa.
 */
export function componiAzione(posto: Posto, da: MossaPilotta): AzioneDiPersona {
  switch (da.t) {
    case 'OFFRI':
      return { t: 'OFFRI', posto, valore: da.valore, seme: da.seme };
    case 'GIOCA':
      return { t: 'GIOCA', posto, carta: da.carta };
    case 'PASSO':
      return { t: 'PASSO', posto };
    case 'KLEISTO':
      return { t: 'KLEISTO', posto };
    case 'XANA_KLEISTO':
      return { t: 'XANA_KLEISTO', posto };
  }
}

/**
 * Il corpo di un tavolo nuovo: i campi di sempre (`tipo`, `posti`, `idempotencyKey`), il `traguardo` scelto (151, 251 o 301: sempre scritto, anche il predefinito) e, SOLO se si sceglie un livello,
 * `robot: { livello }`. Col campo `robot` chi crea siede a un posto estratto dal core e OGNI altro posto e un robot del pool (tre). Mai `classificato`: un tavolo creato da qui non conta, e solo a un
 * tavolo che non conta il gioco ammette i robot (`robotAmmessi` di `regole.ts`, ADR-0212 §2.7-2.8). Un tavolo coi robot e SEMPRE casual (un invito non ha senso: nessun posto da riempire).
 */
export function creaTavoloPilottaBody(a: { tipo: TipoTavolo; idempotencyKey: string; traguardo: Traguardo; livello?: LivelloRobot }) {
  const base = { ...creaTavoloBody({ tipo: a.tipo, posti: POSTI, idempotencyKey: a.idempotencyKey }), traguardo: a.traguardo };
  return a.livello === undefined ? base : { ...base, robot: { livello: a.livello } };
}

/**
 * Le frasi di un rifiuto: i quattro motivi che le regole dicono in `reason` (carta non in mano, seme obbligatorio, taglio obbligatorio, surtaglio obbligatorio) e i codici di `puòAgire`, della licita
 * (`rifiutoChiamata` del motore) e del core che ne hanno una loro. L'elenco e CHIUSO: `ILLEGALE` senza un motivo noto e `AZIONE_MALFORMATA` hanno la frase generica.
 */
export const VOCI_RIFIUTO: VociRifiuto = {
  motivi: MOTIVI,
  codici: [
    'FUORI_TURNO',
    'NON_TUO',
    'PRESA_DA_RACCOGLIERE',
    'STALE_SEQ',
    'FINITA',
    'NON_DISTRIBUITO',
    'THROTTLED',
    'OFFERTA_NON_SUPERIORE',
    'OFFERTA_NON_VALIDA',
    'KLEISTO_DAI_LICITANTI',
    'KLEISTO_SENZA_OFFERTA',
    'KLEISTO_GIA_DETTO',
    'XANA_KLEISTO_FUORI_LICITA',
    'OFFERTA_DOPO_KLEISTO',
  ],
};

// COPIA DICHIARATA di src/lib/tressette/gioco.ts @ bd2f7fbed8b46328c2b8ceae10d65f2229146bda: `eRobotNonDisponibili`, identica (a sua volta di src/lib/briscola/gioco.ts @ 97b42cbc0e02b359c98063b8caa6fe3177973976).
/**
 * Il 503 di «Contro i robot»: il core risponde `503 no_robot_available` quando il pool del gioco non ha abbastanza account liberi, e nessun tavolo nasce. Si riconosce dallo status
 * E dal codice (ovunque stia nel corpo dell'errore): un ALTRO 503 (il servizio che non risponde) resta un guasto generico.
 */
export function eRobotNonDisponibili(errore: unknown): boolean {
  const e = errore as { status?: unknown; data?: unknown } | null;
  if (e === null || typeof e !== 'object' || e.status !== 503) return false;
  try {
    return JSON.stringify(e.data ?? null).includes('no_robot_available');
  } catch {
    return false;
  }
}

/** La linea di un posto: le coppie sono a posti alterni (0-2 contro 1-3). */
export const lineaDelPosto = (posto: Posto): number => posto % 2;

// COPIA DICHIARATA di src/lib/tressette/gioco.ts @ bd2f7fbed8b46328c2b8ceae10d65f2229146bda: `PREFISSO_ROBOT` ed `etichettaDelPosto`, identiche (il criterio e il TIPO del posto, mai il testo).
/** Il prefisso che il game host mette all'etichetta di un robot (kit, `ETICHETTA_BOT`, ADR-0212 §2.1: «🤖 Bot»). La TMA disegna gia il suo `BadgeBot`: l'icona sta una volta sola. */
const PREFISSO_ROBOT = /^\u{1F916}️?\s*/u;

/** L'etichetta di un posto come la zona la stampa: per un posto ROBOT senza il «🤖» iniziale (c'e il `BadgeBot`); per una PERSONA invariata (anche se il suo nickname comincia cosi). */
export function etichettaDelPosto(posto: Pick<PostoPubblico, 'tipo' | 'etichetta'>): string {
  return posto.tipo === 'robot' ? posto.etichetta.replace(PREFISSO_ROBOT, '') : posto.etichetta;
}

/**
 * Il NOME di un posto: SOLO il nickname QLASH che il game host manda in `etichetta` (ADR-0238, I-898, criterio C16 del contratto). ⛔ Mai un ripiego su email o nome anagrafico: di un
 * posto il tavolo legge `tipo` ed `etichetta` e nient'altro. Un posto libero e `libero`; una PERSONA senza nickname e il segnaposto opaco `senzaNome`; un robot senza etichetta e `bot`.
 * Le tre frasi le passa chi chiama (sono del catalogo).
 */
export function nomeDelPosto(posti: readonly PostoPubblico[], p: number, frasi: { libero: string; senzaNome: string; bot: string }): string {
  const pp = posti.find((x) => x.posto === p);
  if (!pp || pp.tipo === 'libero') return frasi.libero;
  const n = etichettaDelPosto(pp).trim();
  if (n !== '') return n;
  return pp.tipo === 'robot' ? frasi.bot : frasi.senzaNome;
}

export const eRobot = (posti: readonly PostoPubblico[], p: number): boolean => posti.find((x) => x.posto === p)?.tipo === 'robot';

/** Quanto tempo resta al turno (secondi), dall'orologio del game host corretto per lo scarto fra l'ora del telefono e la sua; `null` se non c'e un conto per quel posto. */
export function secondiDelTurno(orologio: { posto: number; scadeAt: number } | null, posto: number, adesso: number, scarto: number | null): number | null {
  if (orologio === null || orologio.posto !== posto) return null;
  return Math.max(0, (orologio.scadeAt - (adesso + (scarto ?? 0))) / 1000);
}

const SEMI_FRANCESI: readonly SemeFrancese[] = ['h', 'd', 'c', 's'];

/** La faccia del modulo tavolo per una carta della Pilotta (0..31): seme nell'ordine cuori, quadri, fiori, picche; rango 7, 8, 9, 10, J (11), Q (12), K (13), A (14). */
export function facciaFrancese(c: Carta): FacciaFrancese {
  return { mazzo: 'francese', rango: 7 + rangoDi(c), seme: SEMI_FRANCESI[semeDi(c)]! };
}

/** Il glifo del seme (cuori, quadri, fiori, picche), per il contratto e per le dichiarazioni; non e` testo del catalogo: e` il simbolo del seme. */
export const GLIFO_SEME: readonly string[] = ['♥', '♦', '♣', '♠'];
/** Il seme e` rosso (cuori, quadri) o nero (fiori, picche): serve a colorare il glifo. */
export const SEME_ROSSO = (s: Seme): boolean => s === 0 || s === 1;
